import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../app/store';
import { updateActivity } from '../features/auth/authSlice';

/**
 * Custom hook to track user activity and send heartbeat to server
 * This helps maintain accurate session tracking
 */
export const useActivityTracker = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, sessionToken } = useSelector((state: RootState) => state.auth);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Function to update last activity timestamp
  const updateLastActivity = () => {
    lastActivityRef.current = Date.now();
  };

  // Function to send activity update to server
  const sendActivityUpdate = useCallback(() => {
    if (isAuthenticated && sessionToken) {
      dispatch(updateActivity());
    }
  }, [isAuthenticated, sessionToken, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || !sessionToken) {
      // Clear interval if user is not authenticated
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Activity event listeners
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Add event listeners for user activity
    activityEvents.forEach(event => {
      document.addEventListener(event, updateLastActivity, true);
    });

    // Set up interval to send activity updates every 5 minutes
    intervalRef.current = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;

      // Only send update if user was active in the last 10 minutes
      if (timeSinceLastActivity < 10 * 60 * 1000) {
        sendActivityUpdate();
      }
    }, 5 * 60 * 1000); // 5 minutes

    // Send initial activity update
    sendActivityUpdate();

    // Cleanup function
    return () => {
      // Remove event listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateLastActivity, true);
      });

      // Clear interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, sessionToken, dispatch, sendActivityUpdate]);

  // Return function to manually trigger activity update
  return {
    updateActivity: sendActivityUpdate,
  };
};
